import React, { useEffect, useState } from "react";
import FooterTwo from "../../components/FooterTwo";
import BottomFooter from "../../components/BottomFooter";
import Breadcrumb from "../../components/Breadcrumb";
import HeaderTwo from "../../components/HeaderTwo";
import Preloader from "../../helper/Preloader";
import ScrollToTop from "react-scroll-to-top";
import ColorInit from "../../helper/ColorInit";
import UserGaurd from "../../helper/AuthGaurd";
import TextInput from "../../components/TextInput";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { formatImageName, handleError } from "../../utils/functions";
import axiosInstance from "../../config/axios.config";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { setUser } from "../../store/slice/auth";
import { useTranslation } from "react-i18next";

const schema = yup.object({
  name: yup.string().required("Name is required"),
  email: yup.string().required("Email is required"),
  phoneNumber: yup.string().required("Phone Number is required"),
  address: yup.string().required("Address is required"),
  onlineStatus: yup
    .string()
    .oneOf(["Online", "Offline", "Busy/Away"], "Invalid status")
    .required("Status is required"),
});

const Profile = () => {
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
  });
  const { t } = useTranslation();

  useEffect(() => {
    const getUser = async () => {
      try {
        const { data } = await axiosInstance.get("/user/profile");
        if (!data.error) {
          reset(data.data);
          setProfile(data.data.profile);
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        handleError(error);
      }
    };
    getUser();
  }, [reset]);

  const [selectedImage, setSelectedImage] = useState(null);
  const [profile, setProfile] = useState("");
  const onSubmit = async (values) => {
    try {
      const { data } = await axiosInstance.post(
        "/user/update-profile",
        { ...values, selectedImage },
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      if (!data.error) {
        toast.success(data.message);
        dispatch(setUser(data.data));
        reset();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
    }
  };

  return (
    <UserGaurd>
      <ColorInit color={true} />
      {/* ScrollToTop */}
      <ScrollToTop smooth color="#FA6400" />

      {/* Preloader */}
      <Preloader />

      {/* HeaderTwo */}
      <HeaderTwo category={true} />

      {/* Breadcrumb */}
      <Breadcrumb title={"Update Profile"} />

      <div className="container">
        <div className="row mt-20 justify-content-center">
          <div className="col-md-8">
            <div className="card px-20 py-20">
              <div className="card-body">
                <div className="text-center">
                  <h5 className="text-center mb-40">{t("updateProfile")}</h5>
                </div>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="row">
                    <div className="col-12">
                      <div className="text-center">
                        <div className="position-relative d-inline-block">
                          <img
                            src={
                              profile
                                ? formatImageName(profile)
                                : selectedImage
                                ? URL.createObjectURL(selectedImage)
                                : "/assets/images/user.jpg"
                            }
                            className="w-120 rounded-160 h-120"
                            alt={t("editProfileIcon")}
                          />
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="d-none"
                            id="profileImageUpload"
                          />
                          <label
                            htmlFor="profileImageUpload"
                            className="btn btn-sm btn-primary d-flex justify-content-center align-items-center position-absolute bottom-0 end-0 rounded-circle"
                            style={{ width: "35px", height: "35px", lineHeight: "35px" }}
                          >
                            📷
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <TextInput
                        name={"name"}
                        label={t("name")}
                        type={"text"}
                        className="mb-20"
                        register={register}
                        error={errors.name}
                        placeholder={t("enterName")}
                        isRequired
                      />
                    </div>
                    <div className="col-6">
                      <TextInput
                        name={"email"}
                        label={t("email")}
                        className="mb-20"
                        type={"email"}
                        register={register}
                        error={errors.email}
                        placeholder={t("enterEmail")}
                        isRequired
                      />
                    </div>
                    <div className="col-6">
                      <TextInput
                        name={"phoneNumber"}
                        label={t("phoneNumber")}
                        type={"text"}
                        className="mb-20"
                        register={register}
                        error={errors.phoneNumber}
                        placeholder={t("enterPhone")}
                        isRequired
                      />
                    </div>

                    {/* Status Select Box */}
                    <div className="col-12">
                      <label className="form-label">{t("Status")}</label>
                      <select className="form-control mb-20" {...register("onlineStatus")}>
                        <option value="Online">{t("Online")}</option>
                        <option value="Offline">{t("Offline")}</option>
                        <option value="Busy/Away">{t("Busy/Away")}</option>
                      </select>
                      {errors.status && <span className="text-danger">{errors.status.message}</span>}
                    </div>

                    <div className="col-12">
                      <TextInput
                        name={"address"}
                        label={t("address")}
                        className="mb-20"
                        type={"text"}
                        register={register}
                        error={errors.address}
                        placeholder={t("enterAddress")}
                        isRequired
                      />
                    </div>
                    <div className="col-12 text-center">
                      <button type="submit" className="btn btn-main gap-8 rounded-8 py-16 fw-normal">
                        {t("updateProfile")}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FooterTwo */}
      <FooterTwo />

      {/* BottomFooter */}
      <BottomFooter />
    </UserGaurd>
  );
};

export default Profile;
